import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import { createSignal, For, Show } from 'solid-js'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import SwipeAction from '~/components/SwipeAction'

const SwipeActionPage: Component = () => {
  const [notifications, setNotifications] = createSignal([
    {
      id: 1,
      title: 'GitHub Security',
      message: 'Potential vulnerability found in packages.',
      time: '2m ago',
      type: 'danger',
      icon: 'material-symbols-light:warning-outline'
    },
    {
      id: 2,
      title: 'Revenue Report',
      message: 'Your monthly statement is now available.',
      time: '1h ago',
      type: 'success',
      icon: 'material-symbols-light:trending-up'
    },
    {
      id: 3,
      title: 'Team Sync',
      message: 'Meeting moved to 3:00 PM today.',
      time: '4h ago',
      type: 'info',
      icon: 'material-symbols-light:calendar-month-outline'
    }
  ])

  const [readIds, setReadIds] = createSignal<number[]>([])
  const isRead = (id: number) => readIds().includes(id)
  const toggleRead = (id: number) => setReadIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))

  const deleteNotification = (id: number) => {
    setNotifications(prev => prev.filter(n => n.id !== id))
    setReadIds(prev => prev.filter(x => x !== id))
  }

  return (
    <>
      <Title>Swipe Action - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">Swipe Action</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Interacción gestual de alta fidelidad. Swipe Action permite ejecutar acciones contextuales mediante deslizamientos laterales, optimizado para interfaces móviles y flujos de trabajo de alta
            velocidad en escritorio.
          </p>
        </div>

        <div class="space-y-12">
          {/* Inbox Experience */}
          <Card>
            <Card.Header
              title="Centro de Notificaciones"
              subtitle="Gestión de eventos mediante gestos intuitivos de descarte y archivo."
              icon={<Icon icon="material-symbols-light:notifications-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body class="p-0">
              <div class="divide-y divide-border">
                <For each={notifications()}>
                  {item => (
                    <SwipeAction
                      itemId={item.id}
                      onClick={() => toggleRead(item.id)}
                      leftOptions={[{ icon: 'material-symbols-light:archive-outline', action: () => alert('Archivado'), color: 'success', label: 'Archivar' }]}
                      rightOptions={[
                        {
                          icon: 'material-symbols-light:delete-outline',
                          action: () => deleteNotification(item.id),
                          color: 'danger',
                          label: 'Eliminar'
                        }
                      ]}
                    >
                      <div class={`p-6 flex items-center gap-4 bg-surface hover:bg-surface-hover transition-colors ${isRead(item.id) ? 'opacity-60' : ''}`}>
                        <div
                          class={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0
                                            ${item.type === 'danger' ? 'bg-danger-soft text-danger' : item.type === 'success' ? 'bg-success-soft text-success' : 'bg-info-soft text-info'}`}
                        >
                          <Icon icon={item.icon} size={24} />
                        </div>
                        <div class="flex-1 min-w-0">
                          <div class="flex items-center justify-between gap-2 mb-1">
                            <div class="flex items-center gap-2 min-w-0">
                              <Show when={!isRead(item.id)}>
                                <span class="w-2 h-2 rounded-full bg-primary shrink-0" aria-label="No leído" />
                              </Show>
                              <h4 class="font-bold text-foreground truncate">{item.title}</h4>
                            </div>
                            <span class="text-[10px] font-bold text-foreground-subtle uppercase shrink-0">{isRead(item.id) ? 'Leído' : item.time}</span>
                          </div>
                          <p class="text-sm text-foreground-subtle truncate">{item.message}</p>
                        </div>
                      </div>
                    </SwipeAction>
                  )}
                </For>
              </div>
            </Card.Body>
          </Card>

          {/* Guía de Interacción */}
          <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div class="p-8 bg-success-soft rounded-3xl border border-success/25">
              <div class="flex items-center gap-3 mb-4">
                <Icon icon="material-symbols-light:chevron-right" class="text-emerald-500" size={24} />
                <h3 class="font-bold text-on-success-soft">Gesto a Derecha</h3>
              </div>
              <p class="text-sm text-on-success-soft leading-relaxed italic">Tradicionalmente utilizado para acciones de "Archivo" o "Guardado". Proporciona una sensación de preservación de datos.</p>
            </div>
            <div class="p-8 bg-danger-soft rounded-3xl border border-danger/25">
              <div class="flex items-center gap-3 mb-4">
                <Icon icon="material-symbols-light:chevron-left" class="text-rose-500" size={24} />
                <h3 class="font-bold text-on-danger-soft">Gesto a Izquierda</h3>
              </div>
              <p class="text-sm text-on-danger-soft leading-relaxed italic">Asociado con acciones destructivas como "Eliminar" o "Rechazar". Requiere una intención clara por parte del usuario.</p>
            </div>
          </div>

          {/* API Reference */}
          <Card>
            <Card.Header
              title="API Reference"
              subtitle="Especificaciones técnicas para el componente SwipeAction."
              icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />}
            />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary">leftOptions / rightOptions</td>
                      <td class="px-6 py-4 italic text-xs">SwipeOptionType[]</td>
                      <td class="px-6 py-4 font-mono">[]</td>
                      <td class="px-6 py-4 italic">Acciones por lado. Cada opción acepta color semántico (success/danger…) y label.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-secondary-600">fullSwipe</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 font-mono">true</td>
                      <td class="px-6 py-4">Ejecuta la acción al deslizar por completo cuando el lado tiene una sola opción.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">actionWidth</td>
                      <td class="px-6 py-4 italic text-xs">number</td>
                      <td class="px-6 py-4 font-mono">72</td>
                      <td class="px-6 py-4 italic">Ancho en px de cada botón de acción.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary">onClick</td>
                      <td class="px-6 py-4 italic text-xs">(e) =&gt; void</td>
                      <td class="px-6 py-4 font-mono">-</td>
                      <td class="px-6 py-4">Click sobre el contenido (no dispara tras deslizar ni con el menú abierto).</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">bounceEffect</td>
                      <td class="px-6 py-4 italic text-xs">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4">Rebote elástico al soltar sin ejecutar acción.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default SwipeActionPage
